import { useEffect, useRef } from "react";

export function AiThinkingIcon() {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    function draw() {
      let angle = 0;
      let time = 0;
      let innerWhite = 0.4;
      let outerWhite = 0.7;
      // Define colors
      const colors = [
        "rgba(3, 197, 246, 1)",
        "rgba(232, 99, 255, 1)",
        "rgba(239, 122, 200, 1)",
        "rgba(247, 155, 28, 1)",
      ];
      const canvas = ref.current;
      const ctx = canvas.getContext("2d");
      const offscreenCanvas = document.createElement("canvas");
      const offscreenCtx = offscreenCanvas.getContext("2d");
      function createTurbulencePattern() {
        offscreenCanvas.width = canvas.width;
        offscreenCanvas.height = canvas.height;

        // Create turbulence effect using sine waves
        const imgData = offscreenCtx.createImageData(
          offscreenCanvas.width,
          offscreenCanvas.height,
        );
        const data = imgData.data;

        for (let y = 0; y < offscreenCanvas.height; y++) {
          for (let x = 0; x < offscreenCanvas.width; x++) {
            const n =
              (Math.sin(x * 0.02 /**+ time**/) +
                Math.sin(y * 0.02 /**+ time**/)) *
                127 +
              128;
            const index = (y * offscreenCanvas.width + x) * 4;
            data[index] = n; // Red
            data[index + 1] = n; // Green
            data[index + 2] = n; // Blue
            data[index + 3] = 155; // Alpha (adjust opacity here)
          }
        }
        offscreenCtx.putImageData(imgData, 0, 0);
      }
      function animate() {
        canvas.height = 50;
        canvas.width = 50;
        const { width, height } = canvas;
        const cx = width / 2;
        const cy = height / 2;
        const maxRadius = Math.min(width, height) / 3;

        // Clear the canvas
        ctx.clearRect(0, 0, width, height);

        // Create turbulence pattern
        createTurbulencePattern();

        // Save context and translate to center
        ctx.save();
        ctx.translate(cx, cy);

        // Create conic gradient
        const gradient = ctx.createConicGradient(angle, 0, 0);
        for (let i = 0; i <= colors.length; i++) {
          const colorIndex = i % colors.length;
          gradient.addColorStop(i / colors.length, colors[colorIndex]);
        }

        // Draw the inner circle with conic gradient
        ctx.beginPath();
        ctx.arc(0, 0, maxRadius, 0, Math.PI * 2);
        ctx.closePath();
        ctx.fillStyle = gradient;
        ctx.fill();

        // Apply turbulence effect
        ctx.globalCompositeOperation = "overlay";
        ctx.drawImage(offscreenCanvas, -cx, -cy, width, height);
        ctx.globalCompositeOperation = "source-over";

        // Create a radial gradient for the white center
        const radialGradient = ctx.createRadialGradient(
          0,
          0,
          0,
          0,
          0,
          maxRadius,
        );
        const halfChance = Math.random() > 0.5;
        if (time % 0.08 === 0) {
          if (halfChance) {
            outerWhite += 0.02;
            innerWhite += 0.02;
          } else {
            outerWhite -= 0.02;
            innerWhite -= 0.02;
          }
        }

        // Ensure innerWhite and outerWhite stay within 0 to 1 range
        innerWhite = Math.max(0, Math.min(1, innerWhite));
        outerWhite = Math.max(0, Math.min(1, outerWhite));
        for (let i = 0; i <= colors.length; i++) {
          const colorIndex = i % colors.length;
          radialGradient.addColorStop(0.8, colors[colorIndex]);
        }
        radialGradient.addColorStop(outerWhite, "rgba(255, 255, 255, 1)");
        radialGradient.addColorStop(0.1, "rgba(255, 255, 255, 0)");
        radialGradient.addColorStop(innerWhite, "rgba(255, 255, 255, 0.3)");

        radialGradient.addColorStop(1, "rgba(255, 255, 255, 0)");
        ctx.globalCompositeOperation = "destination-in";
        ctx.fillStyle = radialGradient;
        ctx.beginPath();
        ctx.arc(0, 0, maxRadius, 0, Math.PI * 2);
        ctx.closePath();
        ctx.fill();
        ctx.globalCompositeOperation = "source-over";

        ctx.restore();

        // Draw the outer circle with blurred border
        ctx.save();
        ctx.translate(cx, cy);

        // Parameters for the outer circle
        const gapSize = 1; // Small gap in pixels
        const outerCircleWidth = 3; // Width of the outer circle
        const outerInnerRadius = maxRadius + gapSize;
        const outerOuterRadius = outerInnerRadius + outerCircleWidth;

        // Create radial gradient for the outer circle
        const outerGradient = ctx.createRadialGradient(
          0,
          0,
          outerInnerRadius,
          0,
          0,
          outerOuterRadius,
        );
        //   outerGradient.addColorStop(0, 'rgba(255, 255, 255, 0)'); // Transparent inner edge
        //   outerGradient.addColorStop(0.1, 'rgba(255, 123, 0, 0.1)'); // Transparent outer edge
        //   outerGradient.addColorStop(0.4, 'rgba(255, 123, 0, 0.3)'); // Transparent outer edge
        outerGradient.addColorStop(0, "rgba(255, 123, 0, 0.85)"); // Semi-transparent middle
        outerGradient.addColorStop(0.3, "rgba(255, 123, 0, 0.6)"); // Transparent outer edge
        outerGradient.addColorStop(0.7, "rgba(255, 123, 0, 0.3)"); // Transparent outer edge
        outerGradient.addColorStop(0.9, "rgba(255, 123, 0, 0.1)"); // Transparent outer edge
        outerGradient.addColorStop(1, "rgba(255, 123, 0, 0)"); // Transparent outer edge
        // Draw the outer circle (ring)
        ctx.filter = `blur(${3}px)`;
        ctx.beginPath();
        ctx.arc(0, 0, outerOuterRadius, 0, Math.PI * 2, false);
        ctx.arc(0, 0, outerInnerRadius, 0, Math.PI * 2, true);
        ctx.closePath();

        ctx.fillStyle = outerGradient;
        ctx.fill();

        ctx.restore();

        // Increment time and angle for animation
        time += 0.02;
        angle += 0.02;

        requestAnimationFrame(animate);
      }
      // Start the animation
      animate();
    }
    draw();
  }, []);
  return <canvas ref={ref}></canvas>;
}
